// =============================================================================
// Booking lead time
//
// Dispatch needs warning. Before this, the form only refused a pickup time that
// had already passed *today* - so a booker could ask for a car "now" and then
// call ten minutes later asking where it was. This puts a floor under that.
//
// THE MOVING-GOALPOST PROBLEM, and why there are TWO thresholds.
//
// The naive rule - "must be at least 30 minutes out, checked at submit" - is
// hostile to anyone who takes a few minutes over the form. Pick 17:30 at 17:00,
// spend four minutes on the remaining steps, and the request is refused because
// the floor has crept to 17:34. The booker did nothing wrong.
//
// Rounding the floor up to a 15-minute slot does NOT fix this on its own: at
// 17:00 the floor is 17:30 (already on a boundary) and at 17:01 it is 17:45, so
// the stable window can be as little as a minute.
//
// The fix is to stop conflating the two questions:
//
//   * offeredEarliestMs() - what the FORM offers. Deliberately conservative:
//     the minimum PLUS a form-filling allowance, rounded up to a tidy slot. A
//     booker who takes the earliest slot on offer has FORM_FILL_ALLOWANCE_MINUTES
//     of headroom before their choice could ever go stale.
//
//   * serverFloorMs() - what the API ENFORCES. The true minimum, no allowance.
//     Anything at or beyond it gives dispatch the warning it needs.
//
// So the form is stricter than the server, on purpose. That inverts the usual
// bug (client permits what the server rejects) into a harmless margin, and it
// means the common path - "take the first time offered" - simply cannot fail.
//
//     at 17:00, pickup mode:  server floor 17:30, form offers 17:45
//     booker takes 17:45 and submits at 17:14 -> floor is 17:44, still valid
//
// APPOINTMENT MODE. When the booker gives an appointment time rather than a
// pickup time, dispatch BACK-TIMES the pickup by APPOINTMENT_BUFFER_MINUTES, so
// a 17:30 appointment is really a ~17:15 pickup - half the intended lead. Those
// bookings therefore carry the buffer on top of the minimum.
//
// Single source of truth: the web form and the API both import this, so the two
// can never drift apart on the rule itself.
// =============================================================================

/** Minimum warning dispatch gets before a pickup, in minutes. Enforced. */
export const MIN_LEAD_MINUTES = 30;

/** How far ahead of an appointment dispatch schedules the pickup. Mirrors the
 *  `appointmentBufferMinutes` the booking form sends (15). Appointment-mode
 *  bookings add this to the minimum, because the pickup lands that much earlier
 *  than the time the booker actually typed. */
export const APPOINTMENT_BUFFER_MINUTES = 15;

/** Headroom baked into the times the FORM offers, so a booker who takes the
 *  earliest slot has this long to finish the rest of the form before their
 *  choice could go stale. Not enforced - it only makes the offer generous. */
export const FORM_FILL_ALLOWANCE_MINUTES = 15;

// -----------------------------------------------------------------------------
// EDITING AN EXISTING TRIP
//
// Distinct from the booking floor above: that one governs how soon you may
// create a trip, this one governs how late you may still change one.
//
// Was 24h until Aug 2026. That figure assumed routine scheduled transport, and
// it silently excluded whole departments: Mount Sinai's Cath Lab books same-day
// and next-morning, so the Edit button was hidden on essentially every trip they
// touched. They had been phoning the original booker to make changes, and had
// been reporting it as a permissions fault - it was not, the button simply never
// rendered. Cardiology confirmed (Aug 2026) that changes are almost always the
// PICKUP TIME, raised the day before rather than the day of, which puts their
// real cases in the 12-24h band the old rule blocked outright.
//
// 6h keeps a meaningful floor for dispatch while covering that band with room to
// spare. Cancelling has never been time-limited, and is not affected.
//
// NOTE: this gate is enforced in the UI only. The API deliberately does not
// re-check it (see routes/trip.ts - /update validates the payload shape and the
// `modifyTrip` permission, nothing else), and whether TripLink enforces its own
// floor upstream is UNCONFIRMED. If a late edit ever needs to be genuinely
// prevented rather than merely un-offered, it has to be added server-side.
// -----------------------------------------------------------------------------

/** How close to pickup a trip's details may still be edited. */
export const TRIP_EDIT_MIN_LEAD_HOURS = 6;

/** Same figure in milliseconds, for comparing against a parsed timestamp. */
export const TRIP_EDIT_MIN_LEAD_MS = TRIP_EDIT_MIN_LEAD_HOURS * 60 * 60 * 1000;

/** Offered times are rounded up to a multiple of this, so the booker sees a
 *  tidy clock time ("5:45 PM") rather than an arbitrary one ("5:34 PM"). */
export const LEAD_ROUND_TO_MINUTES = 15;

/** Slack the SERVER allows below its floor, to absorb the seconds between
 *  hitting Submit and the request landing. A rounding allowance, not a second
 *  policy - deliberately far smaller than the minimum itself. */
export const LEAD_SUBMIT_GRACE_MINUTES = 3;

export type LeadTimeMode = "pickup" | "appointment";

/** Minutes of genuine lead a booking mode requires. */
export function requiredLeadMinutes(mode: LeadTimeMode): number {
  return mode === "appointment"
    ? MIN_LEAD_MINUTES + APPOINTMENT_BUFFER_MINUTES
    : MIN_LEAD_MINUTES;
}

/**
 * What the API enforces: the true earliest acceptable instant. No rounding and
 * no allowance - a booking at or after this gives dispatch its warning.
 */
export function serverFloorMs(nowMs: number, mode: LeadTimeMode): number {
  return nowMs + requiredLeadMinutes(mode) * 60_000;
}

/**
 * What the FORM offers as the earliest selectable time: the enforced floor plus
 * a form-filling allowance, rounded UP to the next tidy slot.
 *
 * Always at or beyond `serverFloorMs`, so taking the offer can never produce a
 * booking the server would reject on submit.
 */
export function offeredEarliestMs(nowMs: number, mode: LeadTimeMode): number {
  const step = LEAD_ROUND_TO_MINUTES * 60_000;
  const raw =
    nowMs +
    (requiredLeadMinutes(mode) + FORM_FILL_ALLOWANCE_MINUTES) * 60_000;
  return Math.ceil(raw / step) * step;
}

/**
 * Whether a chosen instant is too soon to book.
 *
 * Measured against the ENFORCED floor, not the offered one, so the form only
 * warns when a time is genuinely unacceptable - never merely because a tidier
 * slot exists. `graceMinutes` is 0 in the UI and LEAD_SUBMIT_GRACE_MINUTES on
 * the server.
 */
export function isTooSoon(
  selectedMs: number,
  nowMs: number,
  mode: LeadTimeMode,
  graceMinutes = 0,
): boolean {
  return selectedMs < serverFloorMs(nowMs, mode) - graceMinutes * 60_000;
}

/** The zone every Sinai trip is scheduled in. Bookings are stored as a
 *  wall-clock date + HH:MM with no zone attached, and they always mean this
 *  one - the API container runs in UTC and a booker could be anywhere. */
const TRIP_TIME_ZONE = "America/New_York";

/** How far ahead of UTC the zone's wall clock reads at a given instant, in ms.
 *  Derived from Intl rather than hardcoded, so DST is handled without a
 *  timezone library or a table of cutover dates. */
function zoneOffsetMs(instantMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TRIP_TIME_ZONE,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instantMs));
  const get = (type: string): number => {
    const v = parts.find((p) => p.type === type)?.value;
    // Intl renders midnight as hour "24" in some engines; normalise it. The
    // hour only: on the 24th the day is "24" too, and was read as day 0 (a
    // trip on the 24th came out an hour or 24 days off).
    return type === "hour" && v === "24" ? 0 : Number(v ?? 0);
  };
  const asIfUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return asIfUtc - instantMs;
}

/**
 * Turn a stored NY wall clock ("2026-08-05", "17:30") into an epoch instant.
 * Returns null when either piece is missing or unparseable - callers treat
 * that as "no time to check" rather than guessing, so a Will Call or a
 * half-filled form is never rejected on a fabricated timestamp.
 *
 * Two passes: interpret the wall clock as if it were UTC, correct by the
 * zone's offset, then re-derive the offset AT that corrected instant and
 * correct again. The second pass is what makes the hour either side of a DST
 * cutover come out right.
 */
export function tripWallClockToMs(
  dateIso: string | null | undefined,
  hhmm: string | null | undefined,
): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(String(dateIso ?? "").trim());
  const t = /^(\d{1,2}):(\d{2})$/u.exec(String(hhmm ?? "").trim());
  if (!d || !t) return null;
  const [hh, mm] = [Number(t[1]), Number(t[2])];
  if (hh > 23 || mm > 59) return null;

  const naiveUtc = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), hh, mm);
  const firstPass = naiveUtc - zoneOffsetMs(naiveUtc);
  return naiveUtc - zoneOffsetMs(firstPass);
}
